import { useMemo, useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Label, Button, FormGroup, Input, Collapse } from "reactstrap";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const PropertySelector = ({
    propertyList,
    selectedProperty,
    onPropertySelectionChange,
    filters,
    onFilterChange,
}) => {
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const currentProperty = useMemo(() =>
        propertyList.find(prop => prop._id === selectedProperty.property_id),
        [propertyList, selectedProperty.property_id]
    );

    const buildingList = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building
            .filter(b => b && b.buildingName)
            .map(b => b.buildingName);
    }, [currentProperty]);

    const propertyOptions = useMemo(() =>
        propertyList.map(prop => ({
            value: prop._id,
            label: `${prop.title}, ${prop.location}`
        })),
        [propertyList]
    );

    const buildingOptions = useMemo(() =>
        buildingList.map(building => ({
            value: building,
            label: building
        })),
        [buildingList]
    );

    const selectedPropertyOption = useMemo(() =>
        propertyOptions.find(option => option.value === selectedProperty.property_id) || null,
        [propertyOptions, selectedProperty.property_id]
    );

    const selectedBuildingOption = useMemo(() =>
        buildingOptions.find(option => option.value === selectedProperty.building_name) || null,
        [buildingOptions, selectedProperty.building_name]
    );

    useEffect(() => {
        if (buildingList.length === 1 && !selectedProperty.building_name && selectedProperty.property_id) {
            onPropertySelectionChange({
                ...selectedProperty,
                building_name: buildingList[0]
            });
        }
    }, [buildingList, selectedProperty, onPropertySelectionChange]);

    useEffect(() => {
        if (currentProperty && selectedProperty.property_id) {
            const updatedProperty = {
                ...selectedProperty,
                address: currentProperty.address || selectedProperty.address,
                postal_code: currentProperty.postal_code || selectedProperty.postal_code,
            };

            if (updatedProperty.address !== selectedProperty.address ||
                updatedProperty.postal_code !== selectedProperty.postal_code) {
                onPropertySelectionChange(updatedProperty);
            }
        }
    }, [currentProperty, selectedProperty, onPropertySelectionChange]);

    const handlePropertyChange = useCallback((selectedOption) => {
        const newPropertyId = selectedOption ? selectedOption.value : '';
        const selectedProp = propertyList.find(prop => prop._id === newPropertyId);

        onPropertySelectionChange({
            property_id: newPropertyId,
            building_name: '',
            address: selectedProp?.address || '',
            postal_code: selectedProp?.postal_code || ''
        });
    }, [onPropertySelectionChange, propertyList]);

    const handleBuildingChange = useCallback((selectedOption) => {
        const newBuildingName = selectedOption ? selectedOption.value : '';

        onPropertySelectionChange({
            ...selectedProperty,
            building_name: newBuildingName
        });
    }, [selectedProperty, onPropertySelectionChange]);

    const handleFilterChange = useCallback((key, value) => {
        onFilterChange({
            ...filters,
            [key]: value
        });
    }, [filters, onFilterChange]);

    const toggleFilters = () => {
        setIsFiltersOpen(!isFiltersOpen);
    };

    const propertySelectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base) => ({
            ...base,
            zIndex: 2
        })
    };

    const buildingSelectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base, state) => ({
            ...base,
            zIndex: 2,
            backgroundColor: buildingList.length === 1 ? '#f8f9fa' : base.backgroundColor,
            cursor: buildingList.length === 1 ? 'not-allowed' : 'default'
        })
    };

    return (
        <Form className='mb-3'>
            {/* Property and Building Selection Row */}
            <Row className="mb-3">
                <Col md={4}>
                    <FormGroup>
                        <Label>Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            name="property_id"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={propertySelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </FormGroup>
                </Col>

                {buildingList.length > 0 && (
                    <Col md={4}>
                        <FormGroup>
                            <Label>Building Name</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={buildingList.length > 1}
                                isDisabled={buildingList.length === 1}
                                name="building_name"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={buildingSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </Col>
                )}

                <Col md={4} className="d-flex align-items-end">
                    <Button
                        color="primary"
                        className="ms-2"
                        onClick={toggleFilters}
                    >
                        <FeatherIcon icon="filter" size={18} style={{ marginRight: '8px' }} />
                        Filters
                    </Button>
                </Col>
            </Row>

            {/* Filters Section */}
            <Collapse isOpen={isFiltersOpen}>
                <Row className="mb-3">
                    <Col md={12}>
                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="all"
                                checked={filters.status === 'all'}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                id="allFilter"
                            />
                            <Label check for="allFilter">
                                All
                            </Label>
                        </FormGroup>
                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="pending"
                                checked={filters.status === 'pending'}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                id="pendingFilter"
                            />
                            <Label check for="pendingFilter">
                                Pending
                            </Label>
                        </FormGroup>
                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="awaiting_auditor"
                                checked={filters.status === 'awaiting_auditor'}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                id="awaitingAuditorFilter"
                            />
                            <Label check for="awaitingAuditorFilter">
                                Awaiting Auditor
                            </Label>
                        </FormGroup>
                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="approved"
                                checked={filters.status === 'approved'}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                id="approvedFilter"
                            />
                            <Label check for="approvedFilter">
                                Approved
                            </Label>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Name</Label>
                            <Input
                                type="text"
                                name="name"
                                value={filters.name || ''}
                                onChange={(e) => handleFilterChange('name', e.target.value)}
                                placeholder="Enter Name"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Unit No</Label>
                            <Input
                                type="text"
                                name="unit_no"
                                value={filters.unitNo || ''}
                                onChange={(e) => handleFilterChange('unitNo', e.target.value)}
                                placeholder="Enter Unit Number"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>Start Date</Label>
                            <Row>
                                <DatePicker
                                    selected={filters.dateRangeStart}
                                    onChange={(date) => handleFilterChange('dateRangeStart', date)}
                                    selectsStart
                                    startDate={filters.dateRangeStart}
                                    endDate={filters.dateRangeEnd}
                                    placeholderText="Start Date"
                                    className="form-control col-6"
                                    dateFormat="MM/dd/yyyy"
                                    popperProps={{
                                        strategy: "fixed",
                                        modifiers: [
                                            {
                                                name: "zIndex",
                                                enabled: true,
                                                phase: "write",
                                                fn: ({ state }) => {
                                                    state.styles.popper.zIndex = "9999";
                                                }
                                            }
                                        ]
                                    }}
                                />
                            </Row>
                        </FormGroup>
                    </Col>
                    <Col md={2}>
                        <FormGroup>
                            <Label>End Date</Label>
                            <Row>
                                <DatePicker
                                    selected={filters.dateRangeEnd}
                                    onChange={(date) => handleFilterChange('dateRangeEnd', date)}
                                    selectsEnd
                                    startDate={filters.dateRangeStart}
                                    endDate={filters.dateRangeEnd}
                                    minDate={filters.dateRangeStart}
                                    placeholderText="End Date"
                                    className="form-control col-6"
                                    dateFormat="MM/dd/yyyy"
                                    popperProps={{
                                        strategy: "fixed",
                                        modifiers: [
                                            {
                                                name: "zIndex",
                                                enabled: true,
                                                phase: "write",
                                                fn: ({ state }) => {
                                                    state.styles.popper.zIndex = "9999";
                                                }
                                            }
                                        ]
                                    }}
                                />
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
            </Collapse>
        </Form>
    );
};

export { PropertySelector };